import { gql } from '@urql/core';

export const GET_COLLECTIONS = gql`
  query GetCollections($options: CollectionListOptions) {
    collections(options: $options) {
      items {
        id
        name
        slug
        position
        isPrivate
        parentId
        parent {
          id
          name
        }
        children {
          id
        }
        breadcrumbs {
          id
          name
          slug
        }
        featuredAsset {
          preview
        }
        productVariants {
          totalItems
        }
        filters {
          code
          args {
            name
            value
          }
        }
        customFields {
          collectionType
          startsAt
          endsAt
        }
      }
      totalItems
    }
  }
`;

export const GET_COLLECTION = gql`
  query GetCollection($id: ID!) {
    collection(id: $id) {
      id
      createdAt
      updatedAt
      name
      slug
      description
      isPrivate
      inheritFilters
      featuredAsset {
        id
        name
        preview
        source
      }
      assets {
        id
        name
        preview
        source
      }
      parent {
        id
        name
      }
      children {
        id
        name
        slug
      }
      breadcrumbs {
        id
        name
        slug
      }
      customFields {
        metaTitle
        metaDescription
        collectionType
        startsAt
        endsAt
        coverImage {
          id
          name
          preview
          source
        }
      }
      translations {
        id
        languageCode
        name
        slug
        description
        customFields {
          metaTitle
          metaDescription
        }
      }
      filters {
        code
        args {
          name
          value
        }
      }
      productVariants(options: { take: 100 }) {
        items {
          id
          name
          sku
          product {
            id
            name
            featuredAsset {
              preview
            }
          }
        }
        totalItems
      }
    }
  }
`;

export const CREATE_COLLECTION = gql`
  mutation CreateCollection($input: CreateCollectionInput!) {
    createCollection(input: $input) {
      id
      name
      slug
      description
      isPrivate
      featuredAsset {
        id
        preview
      }
      parent {
        id
        name
      }
    }
  }
`;

export const UPDATE_COLLECTION = gql`
  mutation UpdateCollection($input: UpdateCollectionInput!) {
    updateCollection(input: $input) {
      id
      name
      slug
      description
      isPrivate
      featuredAsset {
        id
        preview
      }
      parent {
        id
        name
      }
    }
  }
`;

export const DELETE_COLLECTION = gql`
  mutation DeleteCollection($id: ID!) {
    deleteCollection(id: $id) {
      result
      message
    }
  }
`;

export const DELETE_COLLECTIONS = gql`
  mutation DeleteCollections($ids: [ID!]!) {
    deleteCollections(ids: $ids) {
      result
      message
    }
  }
`;

export const MOVE_COLLECTION = gql`
  mutation MoveCollection($input: MoveCollectionInput!) {
    moveCollection(input: $input) {
      id
      name
      parent {
        id
        name
      }
    }
  }
`;

export const BULK_CREATE_COLLECTIONS_FROM_FACET = gql`
  mutation BulkCreateCollectionsFromFacet($facetId: ID!, $parentCollectionId: ID) {
    bulkCreateCollectionsFromFacet(facetId: $facetId, parentCollectionId: $parentCollectionId)
  }
`;

export const GET_COLLECTIONS_WITH_FILTERS = gql`
  query GetCollectionsWithFilters($options: CollectionListOptions) {
    collections(options: $options) {
      items {
        id
        name
        slug
        parentId
        parent {
          id
          name
        }
        breadcrumbs {
          id
          name
          slug
        }
        filters {
          code
          args {
            name
            value
          }
        }
        customFields {
          collectionType
          startsAt
          endsAt
        }
      }
      totalItems
    }
  }
`;

export const GET_COLLECTION_FILTERS = gql`
  query GetCollectionFilters {
    collectionFilters {
      code
      description
      args {
        name
        type
        required
        defaultValue
        list
        ui
        label
        description
      }
    }
  }
`;

export const PREVIEW_COLLECTION_CONTENTS = gql`
  query PreviewCollectionContents(
    $input: PreviewCollectionVariantsInput!
    $options: ProductVariantListOptions
  ) {
    previewCollectionVariants(input: $input, options: $options) {
      items {
        id
        name
        sku
        product {
          id
          name
          featuredAsset {
            preview
          }
        }
      }
      totalItems
    }
  }
`;

export const GET_COLLECTION_CONTENTS = gql`
  query GetCollectionContents($id: ID!, $options: ProductVariantListOptions) {
    collection(id: $id) {
      id
      name
      productVariants(options: $options) {
        items {
          id
          name
          sku
          product {
            id
            name
            featuredAsset {
              preview
            }
          }
        }
        totalItems
      }
    }
  }
`;

// GET_CATEGORIES moved to ./categories.ts in the CATEGORY-SPLIT sprint.
// Categories are a first-class entity now; the query name stays the same
// for consumer continuity but the shape returns `Category` instead of
// `Collection`.

export const GET_COLLECTION_PRODUCT_IDS = gql`
  query GetCollectionProductIds($id: ID!, $options: ProductVariantListOptions) {
    collection(id: $id) {
      id
      productVariants(options: $options) {
        items {
          product {
            id
          }
        }
        totalItems
      }
    }
  }
`;

export const GET_COLLECTION_ASSOCIATIONS = gql`
  query GetCollectionAssociations($collectionId: ID!) {
    collectionAssociations(collectionId: $collectionId) {
      id
      sourceCollectionId
      targetCollectionId
      typeCode
      position
      targetCollection {
        id
        name
        slug
      }
    }
  }
`;

export const SET_COLLECTION_ASSOCIATION = gql`
  mutation SetCollectionAssociation($input: SetCollectionAssociationInput!) {
    setCollectionAssociation(input: $input) {
      id
      sourceCollectionId
      targetCollectionId
      typeCode
      position
      targetCollection {
        id
        name
        slug
      }
    }
  }
`;

export const REMOVE_COLLECTION_ASSOCIATION = gql`
  mutation RemoveCollectionAssociation($id: ID!) {
    removeCollectionAssociation(id: $id) {
      result
      message
    }
  }
`;

export const GET_COLLECTIONS_BY_TYPE = gql`
  query GetCollectionsByType($type: String, $excludeTypes: [String!], $options: CollectionListOptions) {
    collectionsByType(type: $type, excludeTypes: $excludeTypes, options: $options) {
      items {
        id
        name
        slug
        position
        isPrivate
        parentId
        parent {
          id
          name
        }
        children {
          id
        }
        breadcrumbs {
          id
          name
          slug
        }
        featuredAsset {
          preview
        }
        productVariants {
          totalItems
        }
        filters {
          code
          args {
            name
            value
          }
        }
        customFields {
          collectionType
          sortMode
          startsAt
          endsAt
        }
      }
      totalItems
    }
  }
`;

export const DELETE_COLLECTION_SAFE = gql`
  mutation DeleteCollectionSafe($id: ID!) {
    deleteCollectionSafe(id: $id) {
      result
      message
    }
  }
`;

export const DELETE_COLLECTIONS_SAFE = gql`
  mutation DeleteCollectionsSafe($ids: [ID!]!) {
    deleteCollectionsSafe(ids: $ids) {
      result
      message
    }
  }
`;

export const UPDATE_PRODUCT_MANUAL_COLLECTIONS = gql`
  mutation UpdateProductManualCollections($input: UpdateProductManualCollectionsInput!) {
    updateProductManualCollections(input: $input) {
      success
    }
  }
`;
